<template>
  <div class="store-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">门店管理</h2>
        <p class="page-desc">管理您的门店信息，支持多门店运营</p>
      </div>
      <el-button type="primary" size="large" @click="openAddDialog">
        <el-icon>
          <Plus />
        </el-icon>
        新增门店
      </el-button>
    </div>

    <!-- 搜索筛选栏 -->
    <div class="filter-bar">
      <div class="filter-left">
        <el-input v-model="searchKey" placeholder="搜索门店名称或地址" prefix-icon="Search" clearable style="width: 280px"
          @keyup.enter="handleSearch" />
        <el-select v-model="statusFilter" placeholder="全部状态" clearable style="width: 120px">
          <el-option label="营业中" :value="1" />
          <el-option label="已关闭" :value="0" />
        </el-select>
        <el-button type="primary" @click="handleSearch">查询</el-button>
        <el-button @click="resetSearch">重置</el-button>
      </div>
    </div>

    <!-- 门店统计卡片 -->
    <div class="stat-cards">
      <div class="stat-card">
        <div class="stat-icon" style="background: #e8f3ff;">
          <el-icon color="#409eff" :size="28">
            <Shop />
          </el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ storeList.length }}</div>
          <div class="stat-label">总门店数</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background: #e6f7e6;">
          <el-icon color="#67c23a" :size="28">
            <CircleCheck />
          </el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ activeStoreCount }}</div>
          <div class="stat-label">营业中</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background: #fff0e6;">
          <el-icon color="#e6a23c" :size="28">
            <Star />
          </el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ avgRating }}</div>
          <div class="stat-label">平均评分</div>
        </div>
      </div>
    </div>

    <!-- 门店列表 -->
    <div class="store-grid">
      <div v-for="store in storeList" :key="store.id" class="store-card" @click="viewStoreDetail(store)">
        <div class="store-cover">
          <img :src="store.images?.[0] || defaultCover" :alt="store.store_name" />
          <div class="store-status" :class="store.status === 1 ? 'status-active' : 'status-closed'">
            {{ store.status === 1 ? '营业中' : '已关闭' }}
          </div>
        </div>
        <div class="store-info">
          <h3 class="store-name">{{ store.store_name }}</h3>
          <div class="store-address">
            <el-icon>
              <Location />
            </el-icon>
            <span>{{ store.address }}</span>
          </div>
          <div class="store-meta">
            <div class="store-rating">
              <el-rate v-model="store.rating" disabled :text-color="'#ff9a2e'" />
              <span class="rating-score">{{ store.rating }}</span>
            </div>
            <div class="store-price">人均 ¥{{ store.avg_price }}</div>
          </div>
          <div class="store-features">
            <el-tag v-for="(feature, idx) in store.features?.slice(0, 3)" :key="idx" size="small" round>
              {{ feature }}
            </el-tag>
            <el-tag v-if="store.features?.length > 3" size="small" round type="info">
              +{{ store.features.length - 3 }}
            </el-tag>
          </div>
        </div>
        <div class="store-actions" @click.stop>
          <el-button type="primary" link @click="openEditDialog(store)">
            <el-icon>
              <Edit />
            </el-icon> 编辑
          </el-button>
          <el-button type="info" link class="dashboard-btn" size="small"
            @click="goToDashboard(store.id, store.store_name)">
            <el-icon>
              <DataAnalysis />
            </el-icon> 数据大屏
          </el-button>
          <el-popconfirm title="确定要删除该门店吗？" @confirm="handleDelete(store.id)" :teleported="false">
            <template #reference>
              <el-button type="danger" link>
                <el-icon>
                  <Delete />
                </el-icon> 删除
              </el-button>
            </template>
          </el-popconfirm>
        </div>
      </div>

      <div v-if="storeList.length === 0 && !loading" class="empty-state">
        <el-empty description="暂无门店数据，点击右上角新增门店" />
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-wrapper" v-if="pagination.total > pagination.limit">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.limit"
        :total="pagination.total" :page-sizes="[6, 12, 18, 24]" layout="total, sizes, prev, pager, next"
        @size-change="fetchData" @current-change="fetchData" />
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="isEditMode ? '编辑门店' : '新增门店'" width="680px" class="store-dialog"
      destroy-on-close>
      <el-form :model="formData" :rules="formRules" ref="formRef" label-width="100px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="门店名称" prop="store_name" required>
              <el-input v-model="formData.store_name" placeholder="请输入门店名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="门店编号" prop="store_no">
              <el-input v-model="formData.store_no" placeholder="自动生成" disabled />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="地图定位">
          <el-button type="primary" plain @click="openMapPicker">
            <el-icon>
              <MapLocation />
            </el-icon> 在地图上选择位置
          </el-button>
          <div v-if="formData.lng && formData.lat" class="location-info">
            经度: {{ typeof formData.lng === 'number' ? formData.lng.toFixed(6) : formData.lng }},
            纬度: {{ typeof formData.lat === 'number' ? formData.lat.toFixed(6) : formData.lat }}
          </div>
        </el-form-item>

        <el-form-item label="门店地址" prop="address" required>
          <el-input v-model="formData.address" placeholder="请输入详细地址" />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="省份">
              <el-input v-model="formData.province" placeholder="省" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="城市">
              <el-input v-model="formData.city" placeholder="市" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="区域">
              <el-input v-model="formData.district" placeholder="区" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="联系电话">
          <el-input v-model="formData.contact_phone" placeholder="联系电话" />
        </el-form-item>

        <el-form-item label="营业时间">
          <el-time-picker v-model="businessHours" is-range range-separator="至" start-placeholder="开始时间"
            end-placeholder="结束时间" value-format="HH:mm:ss" format="HH:mm:ss" style="width: 100%" />
        </el-form-item>

        <el-form-item label="人均消费">
          <el-input-number v-model="formData.avg_price" :min="0" :precision="2" :step="10" />
        </el-form-item>

        <el-form-item label="评分">
          <div style="display: flex; align-items: center; gap: 10px;">
            <el-rate v-model="formData.rating" :max="5" show-score allow-half />
            <span style="color: #909399;">评价数：</span>
            <el-input-number v-model="formData.rating_count" :min="0" :step="1" controls-position="right"
              style="width: 120px" />
          </div>
        </el-form-item>

        <el-form-item label="门店特色">
          <el-select v-model="formData.features" multiple filterable allow-create default-first-option
            placeholder="请选择或输入门店特色" style="width: 100%">
            <el-option v-for="item in featureOptions" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>

        <el-form-item label="门店图片">
          <div class="image-uploader">
            <el-upload :file-list="imageFiles" action="#" :limit="5" :auto-upload="false" :on-change="handleImageChange"
              :on-remove="handleImageRemove" list-type="picture-card" accept="image/*">
              <el-icon>
                <Plus />
              </el-icon>
              <template #tip>
                <div class="upload-tip">建议尺寸 800x600px，最多上传5张</div>
              </template>
            </el-upload>
          </div>
        </el-form-item>

        <el-form-item label="营业状态">
          <el-switch v-model="formData.status" :active-value="1" :inactive-value="0" active-text="营业中"
            inactive-text="已关闭" />
        </el-form-item>

        <el-form-item label="排序">
          <el-input-number v-model="formData.sort_order" :min="0" :max="999" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 地图选点弹窗 -->
    <el-dialog v-model="mapDialogVisible" title="选择门店位置" width="800px" destroy-on-close>
      <MapPicker v-if="mapDialogVisible" v-model="tempLocation" :mapKey="mapKey" @confirm="handleMapConfirm"
        @cancel="mapDialogVisible = false" />
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { getUploadSignature, uploadToOSS } from '@/utils/uploadUtils'
import { Plus, Edit, Delete, Shop, Location, Star, CircleCheck, MapLocation, DataAnalysis } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import MapPicker from '@/components/MapPicker.vue'

const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
const agentId = userInfo.agent_id || 0
const storeUserId = userInfo.id

const loading = ref(false)
const submitting = ref(false)
const searchKey = ref('')
const statusFilter = ref(null)
const storeList = ref([])
const dialogVisible = ref(false)
const isEditMode = ref(false)
const formRef = ref(null)
const imageFiles = ref([])
const router = useRouter()
const defaultCover = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'

const featureOptions = [
  '可堂食', '可停车', '有包间', '有WiFi', '充电宝',
  '可预约', '可吸烟', '儿童区', '露天座位', '宠物友好'
]

const formData = reactive({
  id: null,
  agent_id: agentId,
  store_id: storeUserId,
  store_no: '',
  store_name: '',
  address: '',
  province: '',
  city: '',
  district: '',
  lng: null,
  lat: null,
  contact_phone: '',
  business_hours: '',
  avg_price: 0,
  rating: 0,
  rating_count: 0,
  features: [],
  images: [],
  status: 1,
  sort_order: 0
})

const mapDialogVisible = ref(false)
const mapKey = ref('')
const tempLocation = reactive({ lng: null, lat: null })

const goToDashboard = (id, name) => {
  router.push(`/store-dashboard?id=${id}&name=${encodeURIComponent(name)}&type=store`)
}

const businessHours = computed({
  get: () => {
    if (formData.business_hours) {
      const times = formData.business_hours.split('-')
      return times.length === 2 ? times : ['09:00:00', '22:00:00']
    }
    return ['09:00:00', '22:00:00']
  },
  set: (val) => {
    if (val && val.length === 2) {
      formData.business_hours = `${val[0]}-${val[1]}`
    } else {
      formData.business_hours = ''
    }
  }
})

const formRules = {
  store_name: [{ required: true, message: '请输入门店名称', trigger: 'blur' }],
  address: [{ required: true, message: '请输入门店地址', trigger: 'blur' }]
}

const pagination = reactive({
  page: 1,
  limit: 12,
  total: 0
})

const activeStoreCount = computed(() => {
  return storeList.value.filter(s => s.status === 1).length
})

const avgRating = computed(() => {
  if (storeList.value.length === 0) return '0.0'
  const validRatings = storeList.value.filter(s => s.rating != null && !isNaN(s.rating))
  if (validRatings.length === 0) return '0.0'
  const sum = validRatings.reduce((acc, s) => acc + parseFloat(s.rating || 0), 0)
  return (sum / validRatings.length).toFixed(1)
})

const generateStoreNo = () => {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
  return `S${year}${month}${day}${random}`
}

const fetchData = async () => {
  loading.value = true
  try {
    const params = {
      agent_id: agentId,
      store_id: storeUserId,
      page: pagination.page,
      limit: pagination.limit,
      search: searchKey.value,
      status: statusFilter.value
    }
    const response = await axios.get(`${config.BASE_URL}/shop/api/group_store_api.php`, { params })
    if (response.data.code === 200) {
      storeList.value = response.data.data.list.map(item => ({
        ...item,
        features: item.features ? (typeof item.features === 'string' ? JSON.parse(item.features) : item.features) : [],
        images: item.images ? (typeof item.images === 'string' ? JSON.parse(item.images) : item.images) : []
      }))
      pagination.total = response.data.data.pagination.total
    } else {
      ElMessage.error(response.data.message || '获取数据失败')
    }
  } catch (error) {
    console.error('获取门店列表失败:', error)
    ElMessage.error('获取数据失败，请稍后重试')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  pagination.page = 1
  fetchData()
}

const resetSearch = () => {
  searchKey.value = ''
  statusFilter.value = null
  pagination.page = 1
  fetchData()
}

const openAddDialog = () => {
  isEditMode.value = false
  resetForm()
  formData.store_no = generateStoreNo()
  dialogVisible.value = true
}

const openEditDialog = (store) => {
  isEditMode.value = true
  Object.assign(formData, {
    ...store,
    features: store.features || [],
    images: store.images || [],
    // 如果营业时间为空，设置默认值
    business_hours: store.business_hours || '09:00:00-22:00:00'
  })
  imageFiles.value = (store.images || []).map((url, index) => ({
    uid: Date.now() + index,
    name: `image-${index}`,
    url: url
  }))
  dialogVisible.value = true
}

const resetForm = () => {
  Object.assign(formData, {
    id: null,
    agent_id: agentId,
    store_id: storeUserId,
    store_no: '',
    store_name: '',
    address: '',
    province: '',
    city: '',
    district: '',
    lng: null,
    lat: null,
    contact_phone: '',
    business_hours: '09:00:00-22:00:00', // 添加默认值
    avg_price: 0,
    rating: 0,
    rating_count: 0,
    features: [],
    images: [],
    status: 1,
    sort_order: 0
  })
  imageFiles.value = []
}

const handleImageChange = async (file, fileList) => {
  if (!file.raw) return
  const isImage = file.raw.type.startsWith('image/')
  if (!isImage) {
    ElMessage.error('只能上传图片文件')
    return false
  }
  const isLt5M = file.raw.size / 1024 / 1024 < 5
  if (!isLt5M) {
    ElMessage.error('图片大小不能超过 5MB')
    return false
  }
  try {
    const signature = await getUploadSignature('image')
    const ossRes = await uploadToOSS(file.raw, signature)
    formData.images.push(ossRes.url)
    imageFiles.value = fileList
    ElMessage.success('上传成功')
  } catch (error) {
    console.error('上传失败:', error)
    ElMessage.error('上传失败')
  }
}

const handleImageRemove = (file, fileList) => {
  const index = formData.images.findIndex(img => img === file.url)
  if (index !== -1) formData.images.splice(index, 1)
  imageFiles.value = fileList
}

const submitForm = async () => {
  if (!formRef.value) return
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    submitting.value = true
    const loadingInstance = ElLoading.service({ text: '保存中...' })
    try {
      const payload = {
        ...formData,
        features: JSON.stringify(formData.features),
        images: JSON.stringify(formData.images)
      }
      delete payload.id
      delete payload.create_time
      delete payload.update_time
      const method = isEditMode.value ? 'put' : 'post'
      let url = `${config.BASE_URL}/shop/api/group_store_api.php`
      if (isEditMode.value) payload.id = formData.id
      const response = await axios[method](url, payload)
      if (response.data.code === 200) {
        ElMessage.success(isEditMode.value ? '更新成功' : '新增成功')
        dialogVisible.value = false
        fetchData()
      } else {
        ElMessage.error(response.data.message || '操作失败')
      }
    } catch (error) {
      console.error('提交失败:', error)
      ElMessage.error('操作失败，请稍后重试')
    } finally {
      submitting.value = false
      loadingInstance.close()
    }
  })
}

const handleDelete = async (id) => {
  const loadingInstance = ElLoading.service({ text: '删除中...' })
  try {
    const response = await axios.delete(`${config.BASE_URL}/shop/api/group_store_api.php`, {
      data: { id, store_id: storeUserId }
    })
    if (response.data.code === 200) {
      ElMessage.success('删除成功')
      fetchData()
    } else {
      ElMessage.error(response.data.message || '删除失败')
    }
  } catch (error) {
    console.error('删除失败:', error)
    ElMessage.error('删除失败，请稍后重试')
  } finally {
    loadingInstance.close()
  }
}

const viewStoreDetail = (store) => {
  openEditDialog(store)
}

const fetchMapKey = async () => {
  try {
    const res = await axios.get(`${config.BASE_URL}/shop/api/get_map_key.php`, {
      params: { store_id: storeUserId }
    })
    if (res.data.code === 200) {
      mapKey.value = res.data.data.key
    } else {
      ElMessage.error('地图服务初始化失败')
    }
  } catch (error) {
    console.error('获取地图密钥失败', error)
  }
}

const openMapPicker = async () => {
  if (!mapKey.value) await fetchMapKey()
  if (!mapKey.value) {
    ElMessage.error('无法获取地图密钥，请联系管理员')
    return
  }
  tempLocation.lng = formData.lng
  tempLocation.lat = formData.lat
  mapDialogVisible.value = true
}

// StoreManagement.vue 和 StoreBranch.vue 中的 handleMapConfirm 方法
const handleMapConfirm = (locationData) => {
  console.log('接收到的地图数据:', locationData)
  if (locationData) {
    formData.lng = locationData.lng
    formData.lat = locationData.lat
    formData.address = locationData.address || ''
    formData.province = locationData.province || ''
    formData.city = locationData.city || ''
    formData.district = locationData.district || ''
  }
  mapDialogVisible.value = false
}

onMounted(() => {
  fetchData()
  fetchMapKey()
})
</script>

<style scoped lang="scss">
.store-management {
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }

    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }
  }

  .filter-bar {
    margin-bottom: 24px;

    .filter-left {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
    }
  }

  .stat-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 32px;

    .stat-card {
      background: #fff;
      border-radius: 16px;
      padding: 20px;
      display: flex;
      align-items: center;
      gap: 16px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      transition: all 0.3s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .stat-icon {
        width: 56px;
        height: 56px;
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .stat-info {
        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #1a1a2e;
          line-height: 1.2;
        }

        .stat-label {
          font-size: 13px;
          color: #8c8c8c;
          margin-top: 4px;
        }
      }
    }
  }

  .store-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
    gap: 24px;
    margin-bottom: 24px;

    .store-card {
      background: #fff;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
      transition: all 0.3s;
      cursor: pointer;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      }

      .store-cover {
        position: relative;
        height: 200px;
        overflow: hidden;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s;
        }

        &:hover img {
          transform: scale(1.05);
        }

        .store-status {
          position: absolute;
          top: 12px;
          right: 12px;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;

          &.status-active {
            background: rgba(103, 194, 58, 0.9);
            color: #fff;
          }

          &.status-closed {
            background: rgba(0, 0, 0, 0.6);
            color: #fff;
          }
        }
      }

      .store-info {
        padding: 16px;

        .store-name {
          font-size: 16px;
          font-weight: 600;
          color: #1a1a2e;
          margin-bottom: 8px;
        }

        .store-address {
          display: flex;
          align-items: flex-start;
          gap: 6px;
          font-size: 13px;
          color: #666;
          margin-bottom: 12px;

          .el-icon {
            flex-shrink: 0;
            margin-top: 2px;
          }

          span {
            flex: 1;
            line-height: 1.4;
          }
        }

        .store-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;

          .store-rating {
            display: flex;
            align-items: center;
            gap: 6px;

            :deep(.el-rate) {
              height: auto;
            }

            .rating-score {
              font-size: 14px;
              font-weight: 600;
              color: #ff9a2e;
            }
          }

          .store-price {
            font-size: 14px;
            color: #f56c6c;
            font-weight: 500;
          }
        }

        .store-features {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
      }

      .store-actions {
        padding: 12px 16px;
        border-top: 1px solid #f0f0f0;
        display: flex;
        gap: 16px;
      }
    }
  }

  .empty-state {
    grid-column: 1 / -1;
    padding: 60px 0;
  }

  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
  }
}

.store-dialog {
  :deep(.el-dialog__body) {
    max-height: 60vh;
    overflow-y: auto;
  }

  .image-uploader {
    :deep(.el-upload--picture-card) {
      width: 100px;
      height: 100px;
    }

    :deep(.el-upload-list--picture-card .el-upload-list__item) {
      width: 100px;
      height: 100px;
    }
  }

  .upload-tip {
    font-size: 12px;
    color: #999;
    margin-top: 8px;
  }
}

.dashboard-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border: none !important;
  color: #fff !important;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  .el-icon {
    margin-right: 4px;
  }
}

.location-info {
  margin-top: 8px;
  color: #67c23a;
  font-size: 13px;
}

@media (max-width: 768px) {
  .store-management {
    .stat-cards {
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;

      .stat-card {
        padding: 12px;

        .stat-icon {
          width: 40px;
          height: 40px;

          .el-icon {
            font-size: 20px !important;
          }
        }

        .stat-info .stat-value {
          font-size: 20px;
        }
      }
    }

    .store-grid {
      grid-template-columns: 1fr;
    }
  }
}

@media (max-width: 480px) {
  .store-management {
    .page-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    .filter-bar .filter-left {
      flex-direction: column;

      .el-input,
      .el-select {
        width: 100% !important;
      }
    }

    .stat-cards {
      grid-template-columns: 1fr;
    }
  }
}
</style>